import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { api, ApiError } from "@/lib/api-client";

const STEP_LABELS: Record<string, string> = {
  planGate: "Checking your plan",
  cfZone: "Creating your domain zone",
  awaitZone: "Waiting for your nameservers",
  dns: "Pointing DNS",
  caddy: "Setting up secure hosting",
  siteConfig: "Updating your site config",
  backend: "Rebuilding your site backend",
  frontend: "Publishing your site",
  email: "Setting up email",
  turnstile: "Configuring spam protection",
  assetUrls: "Updating image links",
  legacyRedirect: "Redirecting your old address",
  verify: "Final checks",
};

function friendlyLabel(step: string): string {
  return STEP_LABELS[step] ?? step;
}

function StatusIcon({ status }: { status: string }) {
  if (status === "done") return <span style={{ color: "var(--color-primary)" }}>✓</span>;
  if (status === "running") return <span className="animate-pulse" style={{ color: "var(--color-accent)" }}>●</span>;
  if (status === "failed") return <span style={{ color: "#dc2626" }}>✗</span>;
  return <span style={{ color: "var(--color-border)" }}>○</span>;
}

export function CustomDomain() {
  const [domain, setDomain] = useState("");
  const [preflight, setPreflight] = useState<Awaited<ReturnType<typeof api.customDomainPreflight>> | null>(null);
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [deniedReason, setDeniedReason] = useState<string | null>(null);
  const [deniedMessage, setDeniedMessage] = useState<string>("");
  const [jobId, setJobId] = useState<string | null>(null);

  const { data: subData } = useQuery({ queryKey: ["subscription"], queryFn: api.subscription });

  const sub = subData?.data;
  const siteReady = !!sub?.tenant_site_id;

  /**
   * A custom domain ships on EVERY plan. This used to read
   * `plan.features.byo_domain`, a key migration 203 deleted — so it was
   * `undefined` for every plan and every customer on every tier hit an
   * "Upgrade to Growth" wall the server would never have imposed.
   *
   * The real gate is whether the subscription is paying: the platform refuses a
   * trialing subscription with `trial_not_converted` (the trial runs on the
   * rumio.world address) and `past_due` deliberately passes, so a failed
   * renewal doesn't yank a live storefront off its own domain.
   */
  const trialing = sub?.status === "trialing";
  const inactive = !!sub && !["active", "past_due", "trialing"].includes(sub.status);

  const { data: status } = useQuery({
    queryKey: ["custom-domain", jobId],
    queryFn: () => api.customDomainStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "completed" || s === "failed" || s === "dead" ? false : 3000;
    },
  });

  async function runPreflight() {
    if (!domain.trim()) return;
    setChecking(true);
    setPreflight(null);
    setError("");
    try {
      setPreflight(await api.customDomainPreflight(domain.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preflight failed");
    } finally {
      setChecking(false);
    }
  }

  async function start() {
    setStarting(true);
    setError("");
    try {
      const { job_id } = await api.promoteDomain(domain.trim());
      setJobId(job_id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        // The server knows exactly why (trial_not_converted / no_subscription /
        // subscription_inactive) and phrases it for the customer — show that
        // rather than guessing at a reason from the client.
        setDeniedReason(e.code || "denied");
        setDeniedMessage(e.message);
      } else {
        setError(e instanceof Error ? e.message : "Failed to start");
      }
    } finally {
      setStarting(false);
    }
  }

  // ── Gate: the subscription isn't paying yet ──
  if (trialing || inactive || deniedReason) {
    const message =
      deniedMessage ||
      (trialing
        ? "Your custom domain is included in your plan and goes live once your trial converts to a paid plan. Until then your site stays on its rumio.world address."
        : "Your subscription isn't active right now, so the domain move is on hold. Sorting out billing will release it.");
    return (
      <Shell title="Use your own domain" subtitle="Included on every plan.">
        <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
          <p className="text-sm">{message}</p>
          <div className="mt-3">
            <Link to="/subscription">
              <Button variant="secondary">View subscription</Button>
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  // ── Site not provisioned yet ──
  if (sub && !siteReady) {
    return (
      <Shell title="Use your own domain" subtitle="Your site is still being built.">
        <p className="text-sm" style={{ color: "var(--color-muted)" }}>
          Come back once your site has finished provisioning, then you can move it to your own domain.
        </p>
      </Shell>
    );
  }

  // ── In progress ──
  if (jobId) {
    const failed = status?.status === "failed" || status?.status === "dead";
    const complete = status?.status === "completed";
    const nameservers = status?.result?.nameservers ?? [];
    return (
      <Shell
        title={complete ? "You're live!" : failed ? "We hit a snag" : "Moving your site"}
        subtitle={
          complete
            ? `Your site now lives at ${status?.result?.newDomain}.`
            : "This can take a while — you'll be signed out and can log back in on the new domain."
        }
      >
        {nameservers.length > 0 && !complete && (
          <div className="mb-4 rounded-md border p-4" style={{ borderColor: "var(--color-primary)" }}>
            <p className="text-sm font-semibold">Set these nameservers at your registrar</p>
            <p className="text-xs" style={{ color: "var(--color-muted)" }}>
              In your domain registrar (e.g. Namecheap → Domain → Nameservers → Custom DNS), replace the nameservers
              with these two. We'll continue automatically once they take effect (can take a few minutes to a few hours).
            </p>
            <ul className="mt-2 font-mono text-sm">
              {nameservers.map((ns) => (
                <li key={ns}>{ns}</li>
              ))}
            </ul>
          </div>
        )}

        <ol className="space-y-2 text-sm">
          {(status?.progress ?? []).map((p) => (
            <li key={p.step} className="flex items-center gap-2">
              <StatusIcon status={p.status} />
              <span style={{ color: p.status === "done" ? "var(--color-muted)" : "var(--color-fg)" }}>
                {friendlyLabel(p.step)}
              </span>
            </li>
          ))}
        </ol>

        {failed && (
          <div className="mt-6 flex items-center gap-3">
            <Button
              onClick={async () => {
                await api.retryCustomDomain(jobId);
              }}
            >
              Retry
            </Button>
            {status?.error && (
              <p className="text-xs" style={{ color: "var(--color-muted)" }}>{status.error.slice(0, 200)}</p>
            )}
          </div>
        )}

        {complete && status?.result?.manualSteps?.length ? (
          <div className="mt-6 rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-sm font-semibold">A few things to finish up</p>
            <ul className="mt-2 space-y-1 text-xs" style={{ color: "var(--color-muted)" }}>
              {status.result.manualSteps.map((m, i) => (
                <li key={i}>• {m}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </Shell>
    );
  }

  // ── Domain entry ──
  return (
    <Shell
      title="Use your own domain"
      subtitle="Move your site from its rumio.world address to a domain you own."
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium">Your domain</span>
          <div className="mt-1 flex gap-2">
            <input
              value={domain}
              onChange={(e) => {
                setDomain(e.target.value);
                setPreflight(null);
              }}
              placeholder="yourbrand.com"
              className="flex-1 rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
            />
            <Button variant="secondary" onClick={runPreflight} disabled={checking || !domain.trim()}>
              {checking ? "Checking…" : "Check"}
            </Button>
          </div>
        </label>

        {preflight && (
          <div
            className="rounded-md border p-3 text-sm"
            style={{ borderColor: preflight.status === "in_account" ? "var(--color-primary)" : "var(--color-border)" }}
          >
            <p>{preflight.recommendation}</p>
            {preflight.authoritative_ns?.length ? (
              <p className="mt-1 font-mono text-xs" style={{ color: "var(--color-muted)" }}>
                current nameservers: {preflight.authoritative_ns.join(", ")}
              </p>
            ) : null}
          </div>
        )}

        <p className="text-xs" style={{ color: "var(--color-muted)" }}>
          You'll need access to your domain registrar to change nameservers. Own the domain? Buy one from any
          registrar (Namecheap, GoDaddy, Google Domains…) first, then come back here.
        </p>

        {error && <p className="text-sm" style={{ color: "#dc2626" }}>{error}</p>}

        <Button onClick={start} disabled={starting || !domain.trim()}>
          {starting ? "Starting…" : "Move my site to this domain"}
        </Button>
      </div>
    </Shell>
  );
}
